import { Component, computed, EventEmitter, inject, input, OnInit, Output, signal } from '@angular/core';
import { CompanyProductSelectionWithMeta, CompanyProductSelectionWithProduct } from 'src/app/store/entity-models';
import { ProductCategory, ProductListItemDTO } from '@iota/inh-core-v1-management-client';
import { collectActiveCategories, isValidSelection } from './registration-period-product-overview.mapper';
import { CompanyProductSelectionDTO } from '@iota/iota-management-portal-v4-client';
import { ModalService } from '@iota/iota-ui-components';
import {
    DialogSelectRegistrationProductsComponent,
    DialogSelectRegistrationProductsData,
    DialogSelectRegistrationProductsResult
} from '../../dialogs/dialog-select-registration-products/dialog-select-registration-products.component';
import { getValueFromStore, isDiscontinued, waitForBooleanValue } from '../../utils';
import { selectedCompanyId, selectedRegistrationPeriod } from 'src/app/store/store';
import { Store } from '@ngrx/store';
import {
    AddUpdateRemoveCompanyProductSelection,
    ClearLatestCompanyProductSelectionIds
} from 'src/app/store/actions/company-product-selection.actions';
import {
    companyProductSelectionActionLoading,
    latestCompanyProductSelectionIds,
    productSelectionsByCompanyId
} from 'src/app/store/reducer/company-product-selection.reducer';
import { UpsertRegistrationPeriod } from 'src/app/store/actions/registration-period.action';
import { registrationPeriodActionLoading } from 'src/app/store/reducer/registration-period.reducer';
import { ProductListService } from '../../services/product-list.service';

@Component({
    standalone: false,
    selector: 'app-registration-period-product-overview',
    templateUrl: './registration-period-product-overview.component.html'
})
export class RegistrationPeriodProductOverviewComponent implements OnInit {
    private readonly productListService = inject(ProductListService);

    readonly showNumberOfSelectedProducts = input(true);
    readonly selectedProducts = input<CompanyProductSelectionWithMeta[]>([]);

    @Output() reloadSelectedProducts = new EventEmitter<void>();

    readonly mockBasketProductExists = this.productListService.mockBasketProductExists;
    readonly error = this.productListService.error;
    readonly newSelectedProducts = signal<CompanyProductSelectionWithProduct[]>([]);

    private readonly selectionsWithProduct = computed<CompanyProductSelectionWithProduct[]>(() => {
        const products = this.productListService.data() ?? [];
        return (this.selectedProducts() ?? []).map((selection) => ({
            ...selection,
            product: products.find((product) => product.productId === selection?.infinkonProductId)
        }));
    });

    readonly activeSelectedProducts = computed(() => this.selectionsWithProduct().filter((selection) => !isDiscontinued(selection)));
    readonly discontinuedSelectedProducts = computed(() => this.selectionsWithProduct().filter(isDiscontinued));

    readonly title = computed(() => {
        const numberOfSelectedProducts = this.selectedProducts()?.length ?? 0;

        return this.showNumberOfSelectedProducts() && numberOfSelectedProducts > 0
            ? $localize`Selected products (${numberOfSelectedProducts})`
            : $localize`Selected products`;
    });

    companyId: string;

    discontinuedSectionTitle = $localize`Discontinued products`;

    editMode = false;
    markedForDelete: string[] = [];

    productSelectionError = false;

    constructor(
        private dialog: ModalService,
        private store: Store
    ) {}

    ngOnInit(): void {
        this.productListService.load();
    }

    reloadProducts() {
        this.productListService.reload();
    }

    trackByProductSelection(_index: number, selection: CompanyProductSelectionWithProduct): string {
        return selection.companyProductSelectionId ?? selection.infinkonProductId;
    }

    markForDelete(companyProductSelectionId: string) {
        if (!this.markedForDelete.includes(companyProductSelectionId)) {
            this.markedForDelete.push(companyProductSelectionId);
        }
    }

    removeNewProduct(productId: string) {
        this.newSelectedProducts.update((products) => products.filter((product) => product.infinkonProductId !== productId));
    }

    undoDelete(companyProductSelectionId: string) {
        if (this.markedForDelete.includes(companyProductSelectionId)) {
            this.markedForDelete.splice(this.markedForDelete.indexOf(companyProductSelectionId), 1);
        }
    }

    openAddProductsDialog() {
        const alreadySelected = this.selectionsWithProduct().concat(this.newSelectedProducts());
        const activeCategories = collectActiveCategories(alreadySelected);

        const data: DialogSelectRegistrationProductsData = {
            alreadySelectedProductIds: alreadySelected.map((selection) => selection.infinkonProductId)
        };
        this.dialog
            .open(DialogSelectRegistrationProductsComponent, { data })
            .afterClosed()
            .subscribe((res: DialogSelectRegistrationProductsResult) => {
                if (!res) {
                    return;
                }

                if (res.selectedProducts && isValidSelection(res.selectedProducts, activeCategories)) {
                    this.addNewProducts(res.selectedProducts);
                } else {
                    this.productSelectionError = true;
                }
            });
    }

    addMockBasketProduct() {
        const firstMockBasketProduct = (this.productListService.data() ?? []).find(
            (product) => product.productCategory === ProductCategory.MOCK_BASKET_PRODUCT
        );

        if (firstMockBasketProduct) {
            this.addNewProducts([firstMockBasketProduct]);
        }
    }

    edit() {
        this.editMode = true;
    }

    private addNewProducts(products: ProductListItemDTO[]) {
        this.newSelectedProducts.update((newProducts) => [
            ...newProducts,
            ...products.map((product) => ({ infinkonProductId: product.productId, product }))
        ]);
    }

    async save() {
        const companyId = await getValueFromStore(this.store, selectedCompanyId);

        const newSelection: CompanyProductSelectionDTO[] = this.newSelectedProducts().map((product) => ({
            companyId,
            infinkonProductId: product.infinkonProductId
        }));
        const deletedSelectionIds = this.markedForDelete;

        if (newSelection.length === 0 && deletedSelectionIds.length === 0) {
            return;
        } else {
            this.store.dispatch(
                AddUpdateRemoveCompanyProductSelection({
                    upsertedProductSelections: newSelection,
                    removedProductSelections: deletedSelectionIds
                })
            );

            await waitForBooleanValue(this.store, companyProductSelectionActionLoading, false);
            const productSelections = await getValueFromStore(this.store, productSelectionsByCompanyId(companyId));

            const registrationPeriod = await getValueFromStore(this.store, selectedRegistrationPeriod);

            // Remove deleted ids
            const updatedProductSelectionIds =
                registrationPeriod.companyProductSelectionIds.filter((id) => !deletedSelectionIds.includes(id)) ?? [];

            const latestProductSelectionIds = await getValueFromStore(this.store, latestCompanyProductSelectionIds);
            const newProductSelectionIds =
                productSelections
                    ?.filter((productSelection) => latestProductSelectionIds?.includes(productSelection?.companyProductSelectionId))
                    ?.map((productSelection) => productSelection.companyProductSelectionId) ?? [];
            updatedProductSelectionIds.push(...newProductSelectionIds);

            // Clear latest ids to avoid bugs when upserting multiple times
            this.store.dispatch(ClearLatestCompanyProductSelectionIds());

            this.store.dispatch(
                UpsertRegistrationPeriod({
                    registrationPeriod: { ...registrationPeriod, companyProductSelectionIds: updatedProductSelectionIds }
                })
            );

            await waitForBooleanValue(this.store, registrationPeriodActionLoading, false);
            this.reloadSelectedProducts.emit();
        }
        this.cancel();
    }

    cancel() {
        this.editMode = false;
        this.markedForDelete = [];
        this.newSelectedProducts.set([]);
    }

    closeSelectionError() {
        this.productSelectionError = false;
    }
}
